import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from '@/app/store/actions';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import {
	TableContainer,
	Table,
	Paper,
	TableHead,
	TableRow,
	TableCell,
	CircularProgress,
	TableBody,
} from '@material-ui/core';
import { getCompanySchools } from '@/app/services/schools/schools';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import Pusher from 'pusher-js';

const SchoolListing = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [isSchoolsLoading, setSchoolsLoading] = useState(false);
	const [schoolCount, setSchoolCount] = useState(null);
	const [viewAll, setViewAll] = useState(false);
	const [schools, setSchools] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(1);
	const [loadingMore, setLoadingMore] = useState(false);
	const user = useSelector(({ auth }) => auth.user);
	const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
		cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
	});

	const GetSchoolListing = () => {
		setSchoolsLoading(true);
		getCompanySchools('', '', '', 1, 'descending')
			.then(({ data }) => {
				setSchools(data.data);
				setSchoolCount(data.total);
				if (data.current_page < data.last_page) {
					setHasMore(true);
					setPage(data.current_page + 1);
				} else {
					setHasMore(false);
				}
			})
			.catch((err) => {
				if (err.response?.data?.message) {
					dispatch(
						Actions.showMessage({
							message: err?.response?.data?.message,
							autoHideDuration: 2500,
							variant: 'error',
						}),
					);
				} else {
					dispatch(
						Actions.showMessage({
							message: 'Failed to load schools',
							autoHideDuration: 2500,
							variant: 'error',
						}),
					);
				}
			})
			.finally(() => {
				setSchoolsLoading(false);
			});
	};
	useEffect(() => {
		const channel = pusher.subscribe(`company_app_${user.data.school.id}`);
		channel.bind('school_update', () => {
			GetSchoolListing();
		});
		channel.bind('school_disabled', () => {
			GetSchoolListing();
		});
		GetSchoolListing();
		return () => {
			pusher.disconnect();
		};
	}, []);

	const handleLoadMore = () => {
		setLoadingMore(true);
		getCompanySchools('', '', '', page, 'descending')
			.then(({ data }) => {
				setSchools(schools.concat(data.data));
				if (data.current_page < data.last_page) {
					setHasMore(true);
					setPage(data.current_page + 1);
				} else {
					setHasMore(false);
				}
			})
			.catch((err) => {
				if (err.response?.data?.message) {
					dispatch(
						Actions.showMessage({
							message: err?.response?.data?.message,
							autoHideDuration: 2500,
							variant: 'error',
						}),
					);
				} else {
					dispatch(
						Actions.showMessage({
							message: 'Failed to load schools',
							autoHideDuration: 2500,
							variant: 'error',
						}),
					);
				}
			})
			.finally(() => {
				setLoadingMore(false);
			});
	};

	return (
		<>
			<div className="flex flex-row justify-between items-center">
				<h4>
					<span className="" style={{ fontSize: '18px', fontWeight: '700' }}>
						Schools
					</span>{' '}
					<span>|</span> <span className="school-count font-extrabold">{schoolCount || ''}</span>
				</h4>
				{!viewAll && schools.length > 5 ? (
					<span>
						<CustomButton
							fontSize="15px"
							onClick={() => {
								setViewAll(true);
							}}
							style={{ justifyContent: 'space-evenly', display: 'flex', alignItems: 'center' }}
							variant="secondary"
						>
							View all <span className="chevron-right-icon">&#8250;</span>
						</CustomButton>
					</span>
				) : (
					''
				)}
			</div>
			<TableContainer className="school-table-cont" component={Paper} id="Schools-Listing">
				<Table className="w-full" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell className="bg-white schoolTableHeader w-1/4 schools-table-heading">Name</TableCell>
							<TableCell className="bg-white schoolTableHeader w-1/4 schools-table-heading">Phone</TableCell>
							<TableCell align="center" className="bg-white schoolTableHeader w-1/4 schools-table-heading">
								Status
							</TableCell>
							<TableCell className="bg-white w-1/4 schools-table-heading" />
						</TableRow>
					</TableHead>
					<TableBody>
						{isSchoolsLoading ? (
							<TableRow>
								<TableCell align="center" className="bg-white" colSpan={8}>
									<CircularProgress size={35} />
								</TableCell>
							</TableRow>
						) : null}
						{!schools[0] && !isSchoolsLoading ? (
							<TableRow>
								<TableCell align="center" className="bg-white no-schools" colSpan={8}>
									No schools
								</TableCell>
							</TableRow>
						) : null}
						{schools.slice(0, viewAll ? schools.length : 5).map((school, key) => {
							return (
								<TableRow key={key}>
									<TableCell className="bg-white name-col" style={{ fontWeight: 'bold' }}>
										{school.name}
									</TableCell>
									<TableCell className="bg-white phone-col schools-table-cell">
										<div className="flex flex-row">
											<img alt="Phone number" src="assets/images/phone.png" style={{ height: '20px' }} />
											{school.phone}
										</div>
									</TableCell>
									<TableCell
										className={`bg-white status-col schools-table-cell ${school.status ? 'green-color' : 'red-color'}`}
									>
										{school.status ? 'Active' : 'Inactive'}
									</TableCell>
									<TableCell align="center" className="bg-white details-col schools-table-cell">
										<button
											className="flex flex-row items-center cursor-pointer"
											onClick={() => {
												navigate(`/editschool/${school.id}`, {
													state: {
														row: school,
													},
												});
											}}
											type="button"
										>
											<span
												className="mr-10 see-details"
												style={{
													color: '#009DFF',
													fontWeight: 700,
													fontSize: '13px',
												}}
											>
												See details
											</span>
											<img alt="See details" src="assets/images/view-arrow.png" />
										</button>
									</TableCell>
								</TableRow>
							);
						})}
						{loadingMore ? (
							<TableRow>
								<TableCell align="center" className="bg-white" colSpan={8}>
									<CircularProgress size={35} />
								</TableCell>
							</TableRow>
						) : null}
					</TableBody>
				</Table>
			</TableContainer>
			{viewAll ? (
				<InfiniteScroll
					dataLength={schools.length}
					hasMore={hasMore}
					next={handleLoadMore}
					scrollableTarget="Schools-Listing"
				/>
			) : null}
		</>
	);
};

export default SchoolListing;
